import { useId } from 'react'
import { addDays, format } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Toggle } from '@highlight/ui'
import { HomeHeadingAction } from '../nested/home-heading'

// The calendar sync reaches back seven days from now, so the seventh day back is only partly loaded.
export const EARLIEST_UPCOMING_DAY = -6
// As far ahead as the full Upcoming view shows.
export const LATEST_UPCOMING_DAY = 6

/** "Today", "Tomorrow", "Yesterday", or the date. */
export function nameUpcomingDay(dayOffset: number): string {
  if (dayOffset === 0) return 'Today'
  if (dayOffset === 1) return 'Tomorrow'
  if (dayOffset === -1) return 'Yesterday'
  return format(addDays(new Date(), dayOffset), 'EEE, MMM d')
}

/**
 * The To-dos Home Upcoming's options: which day it shows, and whether today keeps its ended
 * meetings. Another day always shows all of its meetings, so the toggle only applies to today.
 */
export function UpcomingOptions({
  dayOffset,
  onDayOffsetChange,
  showsEnded,
  onShowsEndedChange,
}: {
  dayOffset: number
  onDayOffsetChange: (dayOffset: number) => void
  showsEnded: boolean
  onShowsEndedChange: (showsEnded: boolean) => void
}) {
  const showsEndedLabel = useId()
  return (
    <div className="type-ui-sm text-foreground flex flex-col">
      <div className="flex items-center justify-between gap-2 py-1 pl-2">
        <span>Day</span>
        <div className="flex items-center">
          <HomeHeadingAction
            icon={ChevronLeft}
            aria-label="Previous day"
            disabled={dayOffset <= EARLIEST_UPCOMING_DAY}
            onClick={() => onDayOffsetChange(dayOffset - 1)}
          />
          <span className="w-24 text-center tabular-nums">{nameUpcomingDay(dayOffset)}</span>
          <HomeHeadingAction
            icon={ChevronRight}
            aria-label="Next day"
            disabled={dayOffset >= LATEST_UPCOMING_DAY}
            onClick={() => onDayOffsetChange(dayOffset + 1)}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <span id={showsEndedLabel} className={dayOffset === 0 ? undefined : 'text-foreground-disabled'}>
          Show past meetings
        </span>
        <Toggle
          size="sm"
          aria-labelledby={showsEndedLabel}
          checked={showsEnded}
          disabled={dayOffset !== 0}
          onCheckedChange={onShowsEndedChange}
        />
      </div>
    </div>
  )
}
